import Typography from '@mui/material/Typography';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { MermaidDiagram } from './MermaidDiagram';
import {
  buildRoutingChart,
  UpstreamChart,
  UpstreamNode,
  UpstreamNodeKind,
  UrlMapDetails,
} from './lbRoutingChart';
import { useUpstreamLoadBalancerChain } from './useUpstreamLoadBalancerChain';

function gcpDetails(entity: Entity): Record<string, unknown> {
  return (entity.spec?.gcpDetails ?? {}) as Record<string, unknown>;
}

function nodeLabel(entity: Entity, kind: UpstreamNodeKind): string {
  const details = gcpDetails(entity);
  const name = (details.name as string | undefined) ?? entity.metadata.name;

  if (kind === 'dnsRecord') {
    const rrdata = (details.rrdata as string[] | undefined) ?? [];
    return rrdata.length > 0
      ? `${name} (${rrdata[0]}${rrdata.length > 1 ? ', …' : ''})`
      : name;
  }
  if (kind === 'forwardingRule') {
    const ip = details.ipAddress as string | undefined;
    const portRange = details.portRange as string | undefined;
    return ip
      ? `${ip}${portRange ? `:${portRange}` : ''}`
      : entity.metadata.name;
  }
  return name;
}

/** Converts the fetched upstream entities/edges into the plain-data structure lbRoutingChart expects. */
function toUpstreamChart(chain: {
  proxies: Entity[];
  forwardingRules: Entity[];
  dnsRecords: Entity[];
  cnames: Entity[];
  edges: Array<{ from: string; to: string }>;
}): UpstreamChart | undefined {
  if (chain.proxies.length === 0) return undefined;

  const nodesFor = (
    entities: Entity[],
    kind: UpstreamNodeKind,
  ): UpstreamNode[] =>
    entities.map(e => ({
      ref: stringifyEntityRef(e),
      label: nodeLabel(e, kind),
      kind,
    }));

  return {
    nodes: [
      ...nodesFor(chain.proxies, 'proxy'),
      ...nodesFor(chain.forwardingRules, 'forwardingRule'),
      ...nodesFor(chain.dnsRecords, 'dnsRecord'),
      ...nodesFor(chain.cnames, 'cname'),
    ],
    edges: chain.edges,
    proxyRefs: chain.proxies.map(stringifyEntityRef),
  };
}

/**
 * Renders a routing diagram for a GCP UrlMap (CNAME/A record/frontend →
 * host → match → backend/redirect), generated live from the ingested spec
 * plus the load balancer's upstream DNS/frontend chain. Renders nothing for
 * any other resource.
 */
export function LoadBalancerRoutingCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const upstreamChain = useUpstreamLoadBalancerChain(entity);

  if (
    annotations['gcp.com/service'] !== 'gcp-lb' ||
    annotations['gcp.com/asset-type'] !== 'compute.googleapis.com/UrlMap'
  ) {
    return null;
  }

  const details = entity.spec?.gcpDetails as UrlMapDetails | undefined;
  if (!details?.pathMatchers) {
    return null;
  }

  const chart = buildRoutingChart(details, toUpstreamChart(upstreamChain));
  if (!chart) {
    return null;
  }

  return (
    <InfoCard title="Routing">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        DNS records, frontends, host rules, path/header matches and their
        backends, generated from Cloud Asset Inventory. ↻ marks a path rewrite.
      </Typography>
      <MermaidDiagram chart={chart} />
    </InfoCard>
  );
}
